import React, {useCallback, useRef, useState} from 'react';
import {TargetItemProps} from '../../fields/hooks/useItemProps';
import {api, useApiContext} from '../../api';
import {Callout, DirectionalHint, TextField} from '@fluentui/react';
import {ResourceDetails} from '../../types/ResourceDetails';
import {getDisplayName} from '../../common/displayName';
import styled from 'styled-components';
import {colors} from '../../styles';
import {useIntl} from 'react-intl';
import {buildIconPropsForTextField} from '../common/iconProps';

type Props = {
  targets: TargetItemProps[];
  onSelect: (id: string) => void;
};

export function SelectorTextInput(props: Props): JSX.Element {
  const [shown, setShown] = useState(false);
  const [text, setText] = useState<string | undefined>();
  const {options, updateOptions} = useOptions(props.targets);
  const ref = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<number>(0);
  const intl = useIntl();

  const selectNext = useCallback(() => {
    if (selected + 1 > options.length - 1) {
      setSelected(0);
    } else {
      setSelected(selected + 1);
    }
  }, [options.length, selected]);

  const selectPrev = useCallback(() => {
    if (selected - 1 < 0) {
      setSelected(options.length - 1);
    } else {
      setSelected(selected - 1);
    }
  }, [options.length, selected]);

  const onSelect = props.onSelect;

  const doSelect = useCallback(
    (index: number) => {
      if (!options[index]) {
        return false;
      }

      onSelect(options[index].id);
      setText('');
      updateOptions();
      setShown(false);
      setSelected(0);
      return true;
    },
    [options, onSelect, updateOptions],
  );

  return (
    <div ref={ref}>
      <TextField
        value={text || ''}
        placeholder={intl.formatMessage({
          id: 'Widget.Item.SearchByText',
          defaultMessage: 'Search and select',
        })}
        iconProps={iconProps}
        onChange={(_, v) => {
          setText(v);
          setShown(!!v);
          updateOptions(v);
        }}
        onFocus={() => {
          setShown(true);
        }}
        onKeyDown={(e) => {
          switch (e.key) {
            case 'ArrowDown':
              selectNext();
              break;
            case 'ArrowUp':
              selectPrev();
              break;
            case 'Enter':
              doSelect(selected);
              break;
            case 'Tab':
              if (doSelect(selected)) {
                e.stopPropagation();
                e.preventDefault();
              }
              break;
          }
        }}
      />
      {shown ? (
        <Callout
          target={ref.current}
          isBeakVisible={false}
          gapSpace={0}
          directionalHint={DirectionalHint.bottomLeftEdge}
          doNotLayer={false}
          onDismiss={() => {
            setShown(false);
          }}>
          {options.map((option, index) => {
            const style =
              index === selected
                ? {
                    borderColor: colors.borderColorLight,
                  }
                : undefined;

            return (
              <Item
                key={`option-${index}`}
                style={style}
                onClick={() => {
                  doSelect(index);
                }}>
                {option.name}
              </Item>
            );
          })}
        </Callout>
      ) : null}
    </div>
  );
}

type Option = {
  id: string;
  name: string;
};

type UseOptions = {
  options: Option[];
  updateOptions: (v?: string) => void;
};

function useOptions(targets: TargetItemProps[]): UseOptions {
  const [options, setOptions] = useState<Option[]>([]);
  const ctx = useApiContext();

  const updateOptions = useCallback(
    (v?: string) => {
      (async () => {
        if (!v) {
          setOptions([]);
          return;
        }

        const merged: Option[] = [];

        for (const target of targets) {
          try {
            const res = await api.list(ctx, target.itemSchema.id, {
              ...target.defaultParams,
              _query: v,
              _selector_text: 1,
              _size: 10,
            });

            const opts = convertToOptions(res.list);
            merged.push(...opts);
          } catch {}
        }
        setOptions(merged);
      })();
    },
    [ctx, targets],
  );

  return {
    options,
    updateOptions,
  };
}

function convertToOptions(list: ResourceDetails[]): Option[] {
  return list.map((item) => {
    return {
      id: item.id,
      name: getDisplayName(item),
    };
  });
}

const iconProps = buildIconPropsForTextField('SearchInTextField');

const Item = styled.div`
  padding: 0.5rem;
  border: 1px solid transparent;
  box-sizing: border-box;

  &:hover {
    background-color: ${colors.bodyColorHovered};
    cursor: pointer;
  }
`;
